import {
  FormControl,
  FormLabel,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChangeEvent, useCallback, useMemo } from "react";
import { usePrescribe } from "src/context/PrescribeProvider/usePrescribe";
import CancelIcon from "@mui/icons-material/Cancel";

interface Props1 {
  canCancel: boolean;
  selectedMedicationId: number | null;
  index: number;
  disabled?: boolean;
}

const MedicationInput = ({
  selectedMedicationId,
  index,
  canCancel,
  disabled = false,
}: Props1) => {
  const { medications, setSelectedMedications } = usePrescribe();

  const helperText = useMemo(() => {
    const strength = medications.find(
      (m) => m.id === selectedMedicationId,
    )?.strength;

    if (strength != null) {
      return `Formula: ${strength}`;
    }

    return null;
  }, [medications, selectedMedicationId]);

  const handleSelected = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSelectedMedications((selected) => {
        return Array(selected.length).fill(Number(value));
      });
    },
    [setSelectedMedications],
  );

  const handleRemoveMedication = useCallback(() => {
    setSelectedMedications((selected) => {
      return [...selected.slice(0, index), ...selected.slice(index + 1)];
    });
  }, [index, setSelectedMedications]);

  return (
    <FormControl fullWidth data-testid={`medication-input-${index + 1}`}>
      <FormLabel id={`medication-label-${index + 1}`}>{`Medication ${
        index + 1
      }: `}</FormLabel>
      <Stack direction="row" gap={1}>
        <TextField
          id={`medication-${index + 1}`}
          disabled={disabled}
          slotProps={{
            input: {
              "aria-labelledby": `medication-label-${index + 1}`,
            },
          }}
          fullWidth
          required
          select
          value={selectedMedicationId ?? ""}
          onChange={handleSelected}
          helperText={helperText}
        >
          {medications.map((med) => (
            <MenuItem key={med.id} value={med.id}>
              {`${med.name} ${med.dosage === "N/A" ? "" : `- ${med.dosage}`}`}
            </MenuItem>
          ))}
        </TextField>

        <Tooltip title="Remove">
          <Typography component={"span"} sx={{ alignSelf: "self-start" }}>
            <IconButton
              aria-label="Remove"
              size="large"
              onClick={handleRemoveMedication}
              disabled={!canCancel}
            >
              <CancelIcon
                fontSize="large"
                color={canCancel ? "error" : "inherit"}
              />
            </IconButton>
          </Typography>
        </Tooltip>
      </Stack>
    </FormControl>
  );
};

export default MedicationInput;
